/**
 * Command Centre fixtures (ADR-0112): a supervisor team with a team inside it, a fixed flow with
 * a sub-agent step, and the recorded event log of one team request (members nested as
 * `member_event`, as `?members=true` streams them) with an approval and both safety checks.
 */
import type { AgUiEvent } from "../agui";
import type { AgentSpec, SpecNode } from "../spec/types";
import { memberStep, withTeam, type Topology } from "../spec/team";
import type { WhatHappened } from "../whatHappened";
import invoice from "./invoice-example.json";

import { COMMAND_RUN, COMMAND_TASK } from "./commandIds";

export { COMMAND_RUN, COMMAND_TASK };

type L = { en: string; ar: string };
const L = (en: string, ar: string): L => ({ en, ar });

function base(name: string, display: L, description: L): AgentSpec {
  const spec = JSON.parse(JSON.stringify(invoice)) as AgentSpec;
  spec.metadata = { ...spec.metadata, name, version: "1.0.0", owner: "team-service", tenant: "acme", description, labels: { display_name: display.en, display_name_ar: display.ar } };
  return spec;
}

function single(name: string, display: L): AgentSpec {
  const spec = base(name, display, L(`${display.en} questions`, `أسئلة ${display.ar}`));
  spec.spec = { ...spec.spec, pattern: "react", graph: { entry: "agent", nodes: [{ id: "agent", type: "llm", label: L("Answer", "الإجابة"), prompt: { template: "Answer: {question}", variables: ["question"] } } as SpecNode, { id: "done", type: "end" }], edges: [{ from: "agent", to: "done" }] } };
  return spec;
}

function team(name: string, display: L, topology: Topology): AgentSpec {
  return withTeam(base(name, display, L(`${display.en} for people`, `${display.ar} للناس`)), topology);
}

const member = (agent: string, role: L) => ({ agent, role, description: role });

const fines = team("traffic-fines", L("Traffic fines", "المخالفات المرورية"), {
  kind: "supervisor",
  members: [member("radar-fines", L("Radar fines", "مخالفات الرادار")), member("parking-fines", L("Parking fines", "مخالفات المواقف"))],
});

const desk = team("service-desk-team", L("Service desk team", "فريق مكتب الخدمة"), {
  kind: "supervisor",
  members: [member("residency-helper", L("Residency", "الإقامة")), member("traffic-fines", L("Traffic fines", "المخالفات المرورية")), member("payments-helper", L("Payments", "المدفوعات"))],
});

const flow = (() => {
  const spec = base("vendor-invoices", L("Vendor invoices", "فواتير الموردين"), L("Reads and pays vendor invoices", "يقرأ فواتير الموردين ويدفعها"));
  const nodes: SpecNode[] = [
    { id: "read", type: "llm", label: L("Read the invoice", "قراءة الفاتورة"), prompt: { template: "Read: {question}", variables: ["question"] } } as SpecNode,
    { id: "check", type: "tool", label: L("Check vendor and order", "التحقق من المورّد والطلب"), tool: "erp", action: "get_invoice" } as SpecNode,
    { id: "pay", type: "subgraph", label: L("Pay it", "الدفع"), agent: "payments-helper", input_map: { question: "{{ state.question }}" } } as SpecNode,
    { id: "reply", type: "llm", label: L("Tell finance", "إبلاغ المالية"), prompt: { template: "Summarise: {question}", variables: ["question"] } } as SpecNode,
    { id: "done", type: "end" },
  ];
  spec.spec = { ...spec.spec, pattern: "custom", graph: { entry: "read", nodes, edges: [{ from: "read", to: "check" }, { from: "check", to: "pay" }, { from: "pay", to: "reply" }, { from: "reply", to: "done" }] } };
  return spec;
})();

/** Every agent of the fixtures, by name. */
export const commandSpecs: Record<string, AgentSpec> = {
  "service-desk-team": desk,
  "traffic-fines": fines,
  "residency-helper": single("residency-helper", L("Residency helper", "مساعد الإقامة")),
  "radar-fines": single("radar-fines", L("Radar fines", "مخالفات الرادار")),
  "parking-fines": single("parking-fines", L("Parking fines", "مخالفات المواقف")),
  "payments-helper": single("payments-helper", L("Payments helper", "مساعد المدفوعات")),
  "vendor-invoices": flow,
};

/** The agents people talk to directly (the catalogue). */
export const commandCatalog = ["service-desk-team", "vendor-invoices"].map((name) => {
  const s = commandSpecs[name] as AgentSpec;
  return { name, version: "1.0.0", display_name: s.metadata.labels?.display_name ?? null, display_name_ar: s.metadata.labels?.display_name_ar ?? null, description: s.metadata.description, risk_tier: s.spec.risk_tier, pattern: s.spec.pattern, channels: ["web"] };
});

const say = (id: string, text: string): AgUiEvent[] => [
  { type: "TEXT_MESSAGE_START", messageId: id, role: "assistant" },
  { type: "TEXT_MESSAGE_CONTENT", messageId: id, delta: text },
  { type: "TEXT_MESSAGE_END", messageId: id },
];

function nested(runId: string, event: AgUiEvent): AgUiEvent {
  return { type: "CUSTOM", name: "member_event", run_id: runId, event: { runId, ...event } };
}

function call(parent: string, i: number, agent: string, label: L, answer: string, inner: AgUiEvent[] = []): { events: AgUiEvent[]; runId: string } {
  const runId = `${parent}--sub-${i}-${memberStep(agent)}-0`;
  return {
    runId,
    events: [
      { type: "CUSTOM", name: "member_started", node: memberStep(agent), member: agent, agent: `${agent}@1.0.0`, label, run_id: runId, kind: "supervisor" },
      nested(runId, { type: "RUN_STARTED" }),
      ...inner,
      ...say(`${runId}-m`, answer).map((e) => nested(runId, e)),
      nested(runId, { type: "RUN_FINISHED" }),
      { type: "CUSTOM", name: "member_finished", node: memberStep(agent), member: agent, label, run_id: runId, status: "completed", answer },
    ],
  };
}

/** One request to the service desk team: residency, the fines team (asking its own members), an approval, payments. */
export function commandReplayEvents(lang: "ar" | "en" = "en"): AgUiEvent[] {
  const ar = lang === "ar";
  const r = COMMAND_RUN;
  const res = call(r, 1, "residency-helper", L("Residency", "الإقامة"), ar ? "تنتهي الإقامة في 2 نوفمبر" : "Residency expires on 2 November");
  const finesRun = `${r}--sub-2-${memberStep("traffic-fines")}-0`;
  const radar = call(finesRun, 1, "radar-fines", L("Radar fines", "مخالفات الرادار"), ar ? "مخالفة واحدة" : "One fine");
  const parking = call(finesRun, 2, "parking-fines", L("Parking fines", "مخالفات المواقف"), ar ? "مخالفة واحدة" : "One fine");
  const finesCall = call(r, 2, "traffic-fines", L("Traffic fines", "المخالفات المرورية"), ar ? "مخالفتان" : "Two fines", [...radar.events, ...parking.events].map((e) => (e.name === "member_event" ? e : nested(finesRun, e))));
  const pay = call(r, 3, "payments-helper", L("Payments", "المدفوعات"), ar ? "تم الدفع" : "Paid");
  return [
    { type: "RUN_STARTED", runId: r },
    { type: "CUSTOM", name: "safety_check", direction: "input", decision: "allow", categories: [] },
    { type: "STEP_STARTED", segment: 1 },
    { type: "CUSTOM", name: "step_data_level", node: "supervisor", level: "internal", route: "local" },
    ...res.events,
    ...finesCall.events,
    { type: "STEP_FINISHED", segment: 1, trail: ["supervisor", memberStep("residency-helper"), "supervisor", memberStep("traffic-fines"), "supervisor"], labels: { supervisor: L("Supervisor", "المشرف") } },
    { type: "CUSTOM", name: "approval_requested", task_id: COMMAND_TASK, node: memberStep("payments-helper"), approver_roles: ["finance-approver"], kind: "approval", reasons: ["needs_approval_step"] },
    { type: "CUSTOM", name: "approval_decided", task_id: COMMAND_TASK, decision: "approve", approver: "alice" },
    { type: "STEP_STARTED", segment: 2 },
    ...pay.events,
    { type: "STEP_FINISHED", segment: 2, trail: [memberStep("payments-helper"), "supervisor"], labels: { supervisor: L("Supervisor", "المشرف") } },
    { type: "CUSTOM", name: "safety_check", direction: "output", decision: "allow", categories: [] },
    ...say(`${r}-answer`, ar ? "تم. سُدّدت المخالفتان وجُدّدت إقامتك." : "Done. Your two fines are paid and your residency is renewed."),
    { type: "RUN_FINISHED", runId: r },
  ];
}

/** The "What happened" view of that request: masked step outputs for the data cards. */
export function commandWhatHappened(): WhatHappened {
  const step = (node: string, label: L, output: Record<string, unknown>) => ({
    id: `${node}-1`,
    node,
    type: "subgraph",
    label,
    visit: 1,
    visits: 1,
    branch: null,
    at: null,
    duration_ms: 1200,
    outcome: "done",
    events: [],
    checks: [],
    badges: [],
    cost_usd: 0,
    tokens: { input: 0, output: 0 },
    failure_route: null,
    child_run: null,
    passages: [],
    data: { input: [], output: [{ node, kind: "output" as const, key: "answer", value: output, masked: true }] },
  });
  return {
    run_id: COMMAND_RUN,
    status: "answered",
    mode: "live",
    environment: "dev",
    reason: null,
    started_at: null,
    finished_at: null,
    duration_ms: 9000,
    requester: "test-user",
    agent: { id: "agent-acme-service-desk-team", name: "service-desk-team", version: "1.0.0" },
    came_in: { channel: "web", trigger: null, conversation: false },
    card: { question: null, answer: null, content: "shown", cost_usd: null, input_tokens: null, output_tokens: null, steps: null },
    changes: [],
    problems: [],
    steps: [
      step(memberStep("residency-helper"), L("Residency", "الإقامة"), { expires: "2026-11-02", fee: "650 SAR", id_number: "[ID_1]" }),
      step(memberStep("traffic-fines"), L("Traffic fines", "المخالفات المرورية"), { fines: 2, total: "1,500 SAR" }),
    ],
    loose: [],
    end: null,
    correction: { replaced_by: null, replaces: null },
    graph: { available: false, entry: null, nodes: [], edges: [], path: [] },
    record_available: true,
    children: [],
  } as unknown as WhatHappened;
}
